import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';
import { useAdminGuard } from '../hooks/useAdminGuard';
import { getStoryImages, uploadStoryImage, deleteStoryImage, StoryImage } from '../config/firebase';
import outreach1 from '../assets/images/our_story/outreach1.jpeg';
import outreach2 from '../assets/images/our_story/outreach4.jpeg';
import outreach3 from '../assets/images/our_story/outreach3.jpeg';
import beforeAfter1_before from '../assets/images/our_story/teeth_before.jpeg';
import beforeAfter1_after from '../assets/images/our_story/teeth_after.jpeg';
import rugie_before from '../assets/images/our_story/burn_before.jpeg';
import rugie_after from '../assets/images/our_story/burn_after.jpeg';

// One image card. Shows the admin override for `slot` if present, else the static `defaultSrc`.
const EditableImage = ({
  slot,
  defaultSrc,
  alt,
  className,
  override,
  isAdmin,
  onChange,
}: {
  slot: string;
  defaultSrc: string;
  alt: string;
  className: string;
  override?: StoryImage;
  isAdmin: boolean;
  onChange: (slot: string, img: StoryImage | null) => void;
}) => {
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    try {
      const img = await uploadStoryImage(slot, file, override?.path);
      onChange(slot, img);
      toast.success('Image updated');
    } catch {
      toast.error('Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!override || !confirm('Revert to the default image?')) return;
    setBusy(true);
    try {
      await deleteStoryImage(slot, override.path);
      onChange(slot, null);
      toast.success('Reverted to default');
    } catch {
      toast.error('Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative group">
      <img src={override?.url ?? defaultSrc} alt={alt} className={className} />
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-2">
          <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={busy}
            className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1 rounded shadow"
          >
            {busy ? '…' : override ? 'Replace' : 'Add'}
          </button>
          {override && (
            <button
              onClick={handleRemove}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded w-7 h-7 flex items-center justify-center shadow"
              aria-label="Revert to default image"
            >
              ✕
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const StoryPage = () => {
  const { isAdmin } = useAdminGuard();
  const [images, setImages] = useState<Record<string, StoryImage>>({});

  useEffect(() => {
    getStoryImages().then(setImages).catch(() => {});
  }, []);

  const onChange = (slot: string, img: StoryImage | null) =>
    setImages((prev) => {
      const next = { ...prev };
      if (img) next[slot] = img;
      else delete next[slot];
      return next;
    });

  // shared props so each card only needs slot/defaultSrc/alt/className
  const cardProps = { isAdmin, onChange };

  return (
    <>
      <SEO
        title="Our Story - Healthier Smile Initiatives"
        description="The story behind Healthier Smile Initiatives and our mission to bring medical and dental care to Sierra Leone. Inspired by real events, driven by a passion to prevent suffering."
      />
      <main className="bg-stone-50">
        <header className="bg-teal-700 text-white text-center py-16 md:py-24">
          <div className="container mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-bold">The Spark That Ignited Our Mission</h1>
            <p className="mt-4 text-lg md:text-xl max-w-3xl mx-auto">Our work is rooted in a real, preventable tragedy. This is the story of why Healthier Smile Initiatives was born.</p>
          </div>
        </header>

        <section id="story-intro" className="py-20 md:py-28">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-stone-200">
              <h3 className="text-2xl font-bold mb-4 text-teal-800">One Woman's Story, A Nation's Crisis</h3>
              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p>Our journey began on December 10, 2022, inspired by the harrowing experience of Madam Rugie, a young lactating mother. She arrived at Connaught Hospital in Freetown in severe pain from a medical and dental abscess that had escalated into Ludwig’s angina—a life-threatening infection.</p>
                <p>Coming from a remote community with no access to medical and dental services, her condition was already critical. Despite rigorous treatment, the infection spread, forcing her to stop nursing her six-month-old baby. The family, facing immense hardship, had to separate the child from her mother. Tragically, while Madam Rugie survived her ordeal, her baby, deprived of care, did not.</p>
                <p>Madam Rugie's story is not an isolated incident. Many in Sierra Leone suffer and die from preventable medical and dental diseases. It was out of this unacceptable reality that Healthier Smile Initiatives was founded: to ensure no one else suffers this fate.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="operations" className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-teal-700">Bringing Hope to Remote Communities</h2>
              <p className="mt-4 text-lg text-stone-600">Healthier Smile Initiatives in operation with improvised materials in the communities where access to medical and dental service is limited.</p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <EditableImage {...cardProps} slot="outreach-1" defaultSrc={outreach1} override={images['outreach-1']} alt="Healthier Smile Initiatives outreach in community" className="w-full h-80 object-cover rounded-lg shadow-md" />
              <EditableImage {...cardProps} slot="outreach-2" defaultSrc={outreach2} override={images['outreach-2']} alt="Healthier Smile Initiatives providing medical and dental care" className="w-full h-80 object-cover rounded-lg shadow-md" />
              <EditableImage {...cardProps} slot="outreach-3" defaultSrc={outreach3} override={images['outreach-3']} alt="Community members receiving medical and dental education" className="w-full h-80 object-cover rounded-lg shadow-md" />
            </div>
          </div>
        </section>

        <section id="before-after-1" className="py-20 md:py-28">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-teal-700">Restoring Smiles, One Person at a Time</h2>
              <p className="mt-4 text-lg text-stone-600">The transformative power of accessible medical and dental care.</p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="text-center">
                <EditableImage {...cardProps} slot="teeth-before" defaultSrc={beforeAfter1_before} override={images['teeth-before']} alt="Woman with medical and dental problems before treatment" className="w-full h-96 object-cover rounded-lg shadow-md mb-4" />
                <h4 className="text-xl font-semibold text-stone-800">Before</h4>
                <p className="text-stone-600">Pain and discomfort from a preventable issue.</p>
              </div>
              <div className="text-center">
                <EditableImage {...cardProps} slot="teeth-after" defaultSrc={beforeAfter1_after} override={images['teeth-after']} alt="Woman with a healthy smile after treatment" className="w-full h-96 object-cover rounded-lg shadow-md mb-4" />
                <h4 className="text-xl font-semibold text-teal-800">After</h4>
                <p className="text-stone-600">The smile has come back, thanks to Healthier Smile.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="before-after-rugie" className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-teal-700">Madam Rugie's Journey to Recovery</h2>
              <p className="mt-4 text-lg text-stone-600">A moment of relief and a renewed smile after her ordeal.</p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="text-center">
                    <EditableImage {...cardProps} slot="rugie-before" defaultSrc={rugie_before} override={images['rugie-before']} alt="Madam Rugie before treatment" className="w-full h-96 object-cover rounded-lg shadow-md mb-4" />
                    <h4 className="text-xl font-semibold text-stone-800">A Moment of Hardship</h4>
                </div>
                <div className="text-center">
                    <EditableImage {...cardProps} slot="rugie-after" defaultSrc={rugie_after} override={images['rugie-after']} alt="Madam Rugie smiling after treatment" className="w-full h-96 object-cover rounded-lg shadow-md mb-4" />
                    <h4 className="text-xl font-semibold text-teal-800">A Smile of Hope</h4>
                    <p className="text-stone-600">Madam Rugie, a lactating mother of a 6-month-old baby, just after a moment of wound dressing. The smile is awesome.</p>
                </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default StoryPage;
